import type { AnnotationTerm, ConvertedMetadata, EntityType } from '@sap-ux/vocabularies-types';
import type { LineItem } from '@sap-ux/vocabularies-types/vocabularies/UI';
import { UIAnnotationTerms, UIAnnotationTypes } from '@sap-ux/vocabularies-types/vocabularies/UI';
import type {
    FacetConfigs,
    FacetConfig,
    FileData,
    GenerateAppSchemaParameters,
    ExtensionLogger
} from '@sap/ux-specification-types';
import {
    ActionType,
    ArtifactType,
    SchemaKeyName,
    ControlType,
    SectionType,
    FioriElementsVersion,
    ManifestSection,
    FacetBase,
    FACETTITLEPREFIX,
    DefinitionName,
    MANIFESTPATH,
    LogSeverity,
    PropertyName,
    SchemaTag,
    v2
} from '@sap/ux-specification-types';
import {
    getDataFieldDescription,
    getObjectPageHeaderFacets,
    updatePropertyIndices,
    getObjectPageFacets,
    createDefaultSection,
    getObjectPageFacetSection,
    DEFINITION_LINK_PREFIX,
    addFieldsType,
    addDefinitionForRelatedFacetKeys,
    addSectionTitle,
    addSectionDescription,
    handleAddressContact,
    handleForm,
    addPatternForBindingChangeOfEnumOP,
    getActionNameAndId,
    addFooterActions,
    addHeaderActions,
    addCommonHeaderSchema,
    prepareRef,
    determineEntitySetOfEntityType,
    addEnumForSingleTabVariant,
    determineDataTypeOfTarget,
    DataType,
    convertSyncRulePathToJsonPath,
    getDefinitionKey,
    appendJsonPathSegment
} from '../../common';
import type { ManifestPathParams, ManifestPropertyResolution } from '../../common';
import {
    addLineItemsType,
    addTableColumnExtensions,
    type DefinitionAdapter,
    getExportTablesPropertiesAdapter
} from './utils';
import { filterFlexChanges, findObjectPageV2 } from '../import';
import { log } from '../../../extensionLogger';
import i18next from 'i18next';
import { initI18n } from '../../../i18n/i18n';
import { SectionTypeV2 } from '../types';
import type { Definition } from 'typescript-json-schema';
import { getStableIdPartFromDataField } from '../../v4/utils/StableIdHelper';
import type { EntityTypeAnnotationsBase_UI } from '@sap-ux/vocabularies-types/vocabularies/UI_Edm';
import { addManifestPathsToProperties, getPageManifestPath } from '../utils';
import { pageTypes } from '../export/factory';
import { SAPUI5_VIEW_EXTENSION_OBJECT_PAGE } from '@sap/ux-specification-types/src/v2';
import { getViewExtensionsPath } from '../application';

const ROOT_PROPERTIES_ORDER: string[] = ['header', 'sections', 'footer'];

/**
 * Adds flexChange properties for sub(sections).
 *
 * @param appSchema - app schema in general
 * @param schemaSection - current section
 * @param property - property that will be enhanced
 * @param sectionType - type of section
 */
function addFlexChangeForSection(
    appSchema: Definition,
    schemaSection: Definition,
    property: string,
    sectionType: SectionTypeV2
) {
    schemaSection.properties[property][SchemaTag.artifactType] = ArtifactType.FlexChange;
    let controlType: ControlType;
    switch (sectionType) {
        case SectionTypeV2.Section:
        case SectionTypeV2.SectionForm:
        case SectionTypeV2.SectionChart:
        case SectionTypeV2.SectionTable:
            controlType = ControlType.Section;
            break;
        case SectionTypeV2.SubSection:
            controlType = ControlType.SubSection;
            break;
        case SectionTypeV2.SubSectionForm:
            controlType = ControlType.Group;
            break;
        case SectionTypeV2.SubSectionChart:
            controlType = ControlType.SmartChart;
            break;
        case SectionTypeV2.SubSectionTable:
            controlType = ControlType.SmartTable;
            break;
        case SectionTypeV2.HeaderSection:
        case SectionTypeV2.HeaderSectionAddress:
        case SectionTypeV2.HeaderSectionChart:
        case SectionTypeV2.HeaderSectionDataPoint:
        case SectionTypeV2.HeaderSectionForm:
            controlType = ControlType.ObjectPageHeaderSection;
            break;
    }
    if (!controlType) {
        return;
    }
    addPatternForBindingChangeOfEnumOP(appSchema, schemaSection, property);
    schemaSection.properties[property][SchemaTag.controlType] = controlType;
}

/**
 * Callback for adding a single action to headerActions of V2.
 *
 * @param {EntityType} entityType - current entity type
 * @param {object} appSchema Schema of the app
 * @param identification - UI.Identification annotation as present in AVT ConvertedMetadata
 * @param headerActions - list of header actions, to be enhanced
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 */
function addHeaderActionCallBackV2(
    entityType,
    appSchema: Definition,
    identification: EntityTypeAnnotationsBase_UI['Identification'] | undefined,
    headerActions: Definition,
    generateParameters: GenerateAppSchemaParameters
): any {
    identification?.forEach((item) => {
        if (!item['Determining'] && item.$Type === UIAnnotationTypes.DataFieldForAction) {
            const { actionName, actionId, namespace } = getActionNameAndId(item, generateParameters.serviceAVT);
            //actionId is used as key in V2, as this is the right part for building the full stable ID
            if (!actionId) {
                return;
            }
            const description = (item.Label || actionName) as string;
            const actionDefinitionKey = `ObjectPageHeaderAction<${prepareRef(actionId)}>`;
            appSchema.definitions[actionDefinitionKey] = JSON.parse(
                JSON.stringify(appSchema.definitions['ObjectPageHeaderAction'])
            );
            const convertedIdentification = item.fullyQualifiedName.replace(
                `@${UIAnnotationTerms.Identification}`,
                `/@${UIAnnotationTerms.Identification}`
            );
            appSchema.definitions[actionDefinitionKey][SchemaTag.annotationPath] = `/${convertedIdentification}`;
            (appSchema.definitions[actionDefinitionKey] as Definition)[SchemaTag.actionType] = ActionType.Annotation;
            (appSchema.definitions[actionDefinitionKey] as Definition).description = description;
            appSchema.definitions[actionDefinitionKey][SchemaTag.keys] = [
                { name: SchemaKeyName.action, value: actionName }
            ];
            if (namespace) {
                appSchema.definitions[actionDefinitionKey][SchemaTag.target] = namespace;
            }

            headerActions.properties[actionId] = {
                $ref: `${DEFINITION_LINK_PREFIX}${actionDefinitionKey}`
            };
        }
    });
}

/**
 * Callback for adding a single action to footerActions of V2.
 *
 * @param {EntityType} entityType - current entity type
 * @param {Definition} appSchema Schema of the app
 * @param {Definition} footerActions - list of footer actions, to be enhanced
 * @param footerActions.type - The type of the footer actions
 * @param footerActions.properties - The properties of the footer actions.
 * @param footerActions.description - A description of the footer actions.
 * @param footerActions.additionalProperties - Indicates whether additional properties are allowed.
 * @param footerActions.isViewNode - Specifies if the footer actions are part of the view node.
 * @param identification - converted UI.Identification term
 */
function addFooterActionCallBackV2(
    entityType,
    appSchema: Definition,
    footerActions: {
        type: string;
        properties: {};
        description: string;
        additionalProperties: boolean;
        isViewNode: boolean;
    },
    identification
): any {
    identification.forEach((item) => {
        if (item['Determining'] && item.$Type === UIAnnotationTypes.DataFieldForAction) {
            const actionId = getStableIdPartFromDataField(item).split('::').splice(1).join('::');
            const description = getDataFieldDescription(item, entityType);
            const actionDefinitionKey = prepareRef(`${DefinitionName.ObjectPageFooterAction}<${actionId}>`);
            const convertedIdentification = item.fullyQualifiedName.replace(
                `@${UIAnnotationTerms.Identification}`,
                `/@${UIAnnotationTerms.Identification}`
            );
            try {
                appSchema.definitions[actionDefinitionKey] = {
                    ...JSON.parse(JSON.stringify(appSchema.definitions[DefinitionName.ObjectPageFooterAction])),
                    description,
                    annotationPath: `/${convertedIdentification}`
                };
            } catch {
                throw new Error(
                    i18next.t('INCONSISTENTSCHEMA', {
                        function: 'addFooterActionCallBackV2',
                        definition: 'ObjectPageFooterAction'
                    })
                );
            }

            footerActions.properties[actionId] = {
                $ref: `${DEFINITION_LINK_PREFIX}${actionDefinitionKey}`
            };
        }
    });
}

/**
 * Method ensures that definitions for custom columns are created for passed entitySet.
 *
 * @param {object} appSchema App schema in general
 * @param {object} manifest manifest.json of the app
 * @param {string} entitySetName Entity set name.
 * @param {string} entityTypeName Entity type name.
 * @param {ConvertedMetadata} oDataServiceAVT Complete service information, as returned by annotation vocabularies tool
 * @param {FacetConfig} facet information
 * @param {string} facetKey - key of the facet, as listed in FacetConfigs
 * @param {FileData[]} [fragments] Array with XML fragments.
 * @returns {string} Name of custom columns definition.
 */
function ensureCustomColumnDefinitionExists(
    appSchema: Definition,
    manifest: object,
    entitySetName: string,
    entityTypeName: string,
    oDataServiceAVT: ConvertedMetadata,
    facet: FacetConfig,
    facetKey: string,
    fragments?: FileData[]
): string {
    let suffix, customColumnsDefinitionName;
    if (facet.ID && facet.Label) {
        suffix = facet.ID + facet.Label.replace(/\s+/g, '');
    } else if (facet.ID) {
        suffix = facet.ID;
    } else if (facet.Label) {
        suffix = facet.Label.replace(/\s+/g, '');
    }
    if (suffix) {
        customColumnsDefinitionName = `${DefinitionName.CustomColumns}<${entitySetName}><${facetKey}><${suffix}>`;
    } else {
        customColumnsDefinitionName = `${DefinitionName.CustomColumns}<${entitySetName}><${facetKey}>`;
    }

    if (appSchema.definitions[customColumnsDefinitionName]) {
        // Definition is already created - we do not need to create it again
        return customColumnsDefinitionName;
    }
    // Create type specific 'TableCustomColumn' definition - use entity set as type.
    const customColumnDefinitionName = `${DefinitionName.CustomColumn}<${entitySetName}>`;
    try {
        appSchema.definitions[customColumnDefinitionName] = JSON.parse(
            JSON.stringify(appSchema.definitions['TableCustomColumn'])
        );
    } catch {
        throw new Error(
            i18next.t('INCONSISTENTSCHEMA', {
                function: 'ensureCustomColumnDefinitionExists',
                definition: 'TableCustomColumn'
            })
        );
    }
    // Update definitions for type specific custom columns
    const pageKeys = [];
    const entityType = oDataServiceAVT?.entityTypes.find((et) => et.name === entityTypeName);
    const v2Page = findObjectPageV2(manifest[ManifestSection.generic].pages, entitySetName, pageKeys);
    addTableColumnExtensions(
        appSchema,
        entityType,
        v2Page,
        manifest,
        fragments,
        customColumnsDefinitionName,
        customColumnDefinitionName,
        facet.ID
    );

    return customColumnsDefinitionName;
}

/**
 * Method fills new app schema section with properties.
 *
 * @param {Definition} appSchema App schema in general
 * @param {object} appSchemaSection app schema section to be filled
 * @param {SectionTypeV2} sectionType (sub)section type
 * @param {SectionTypeV2} mainSectionType main section type, used to fill app schema section
 */
function addAppSchemaDefinition(
    appSchema: Definition,
    appSchemaSection: Definition,
    sectionType: SectionTypeV2,
    mainSectionType: SectionTypeV2
): void {
    appSchemaSection.properties = JSON.parse(
        JSON.stringify((appSchema.definitions[mainSectionType] as Definition).properties)
    );
    if (appSchemaSection.properties.visible) {
        addFlexChangeForSection(appSchema, appSchemaSection, 'visible', sectionType);
    }
}

/**
 * Method adds reference to section property key.
 *
 * @param {object} sections - schema of current sections definition
 * @param {string} key section key which will contain the reference
 * @param {string} definitionKey key which the section should point to
 */
function addSectionReference(sections: Definition, key: string, definitionKey: string) {
    sections.properties[key] = { $ref: `${DEFINITION_LINK_PREFIX}${definitionKey}` };
}

/**
 * Reuse function for all table types, copies and adapts the generic definition.
 *
 * @param tableType - Table type
 * @param facetId - facet ID, as derived for the given section
 * @param facet - facet or section information
 * @param appSchema - app-specific schema, to be updated
 * @param tablePropertiesAdapter - function that should be applied to the table definition
 */
function addTableDefinition(
    tableType: string,
    facetId: string,
    facet: FacetConfig,
    appSchema: Definition,
    tablePropertiesAdapter: DefinitionAdapter
): void {
    const schemaId = prepareRef(`${tableType}<${facetId}>`);
    try {
        appSchema.definitions[schemaId] = JSON.parse(JSON.stringify(appSchema.definitions[tableType]));
    } catch {
        throw new Error(
            i18next.t('INCONSISTENTSCHEMA', { function: 'addTableDefinition', definition: `${tableType}` })
        );
    }
    tablePropertiesAdapter(appSchema.definitions[schemaId] as Definition);
    appSchema.definitions[schemaId]['properties']['columns']['$ref'] = DEFINITION_LINK_PREFIX + facetId;
    (appSchema.definitions[schemaId] as Definition).properties.toolBar['$ref'] =
        `${DEFINITION_LINK_PREFIX}ObjectPageToolBar<${facetId}>`;
    appSchema.definitions[schemaId][SchemaTag.annotationPath] = facet.annotationPath;
    if (facet.ID !== undefined) {
        (appSchema.definitions[schemaId] as Definition).title = FACETTITLEPREFIX + facet.ID;
        appSchema.definitions[schemaId][SchemaTag.keys] = [{ name: `ID`, value: facet.ID }];
    }
    (appSchema.definitions[schemaId] as Definition).description = DefinitionName.Table;

    //adjust quickVariantSelection
    (appSchema.definitions[schemaId] as Definition).properties['quickVariantSelection']['$ref'] =
        `${DEFINITION_LINK_PREFIX}QuickVariantSelectionOP<${facetId}>`;
    appSchema.definitions[`QuickVariantSelectionOP<${facetId}>`] = JSON.parse(
        JSON.stringify(appSchema.definitions['QuickVariantSelectionOP'])
    );
    appSchema.definitions[`QuickVariant<${facetId}>`] = JSON.parse(
        JSON.stringify(appSchema.definitions['QuickVariant'])
    );
    (
        (appSchema.definitions[`QuickVariantSelectionOP<${facetId}>`] as Definition).properties.variants as Definition
    ).patternProperties['^[0-9]+$']['$ref'] = `${DEFINITION_LINK_PREFIX}QuickVariant<${facetId}>`;

    addEnumForSingleTabVariant(facet.entityType, appSchema, `QuickVariant<${facetId}>`);
}

/**
 * Adds definitions for line items in object page sections to the app schema.
 *
 * @param pageKeys Array path to target page (e.g., ['parentPage', 'currentPage'])
 * @param generateParameters - list of API input parameters
 * @param facetKey - key of the facet, as listed in FacetConfigs
 * @param appSchema - app schema in general
 * @param facet - the given facet from the UI annotations
 * @param facets - list of all facets
 * @param sectionType (sub)section type
 */
function handleLineItem(
    pageKeys: string[],
    generateParameters: GenerateAppSchemaParameters,
    facetKey: string,
    appSchema: Definition,
    facet: FacetConfig,
    facets: FacetConfigs,
    sectionType?: SectionTypeV2
): void {
    if (facet.base !== FacetBase.LineItem || !facetKey) {
        //no properties
        return;
    }
    const navigationProperty = facetKey.split('::')[0];
    const schemaIdForOpSection = `${sectionType}<${facetKey}>`;

    appSchema.definitions[schemaIdForOpSection] = createDefaultSection(appSchema, facet, facetKey);
    const appSchemaSection = appSchema.definitions[schemaIdForOpSection];
    addAppSchemaDefinition(appSchema, appSchemaSection, sectionType, SectionTypeV2.SectionTable);
    const tableDefinition: Definition = JSON.parse(
        JSON.stringify(appSchema.definitions[SectionTypeV2.SectionTable]['properties'][PropertyName.table])
    );
    tableDefinition.anyOf = [
        {
            $ref: `${DEFINITION_LINK_PREFIX}ObjectPageResponsiveTableWithMultiSelect<${facetKey}>`
        },
        {
            $ref: `${DEFINITION_LINK_PREFIX}ObjectPageResponsiveTableWithInlineDelete<${facetKey}>`
        },
        {
            $ref: `${DEFINITION_LINK_PREFIX}ObjectPageAnalyticalTable<${facetKey}>`
        },
        {
            $ref: `${DEFINITION_LINK_PREFIX}ObjectPageGridTable<${facetKey}>`
        },
        {
            $ref: `${DEFINITION_LINK_PREFIX}ObjectPageTreeTable<${facetKey}>`
        }
    ];
    appSchemaSection.properties.table = tableDefinition;
    appSchemaSection[SchemaTag.annotationPath] = facet.annotationPath;
    addSectionTitle(facet, appSchemaSection, facetKey);
    addSectionDescription(facet, appSchemaSection, facetKey);

    // Note that facetKey already has replaced all '/' by '::'.However, the @-symbols resulting from
    // the annotation terms are still contained in facetKey. Therefore, we remove these symbols.
    // Suffix '::Table' is added to get the controlId of the SmartTable
    const smartTableControlId = `${facetKey.replaceAll('@', '')}::Table`;
    // Retrieve a function that will adjust the table definition so that it handles properties exportToExcel and enableExport correctly.
    const exportTablePropertiesAdapter = getExportTablesPropertiesAdapter(
        generateParameters.flex ?? [],
        smartTableControlId
    );

    addTableDefinition(
        'ObjectPageResponsiveTableWithMultiSelect',
        facetKey,
        facet,
        appSchema,
        exportTablePropertiesAdapter
    );
    addTableDefinition(
        'ObjectPageResponsiveTableWithInlineDelete',
        facetKey,
        facet,
        appSchema,
        exportTablePropertiesAdapter
    );
    addTableDefinition('ObjectPageGridTable', facetKey, facet, appSchema, exportTablePropertiesAdapter);
    addTableDefinition('ObjectPageTreeTable', facetKey, facet, appSchema, exportTablePropertiesAdapter);
    addTableDefinition('ObjectPageAnalyticalTable', facetKey, facet, appSchema, exportTablePropertiesAdapter);

    let customColumnDefinitionKey;
    // Find target entity
    const targetEntitySet = determineEntitySetOfEntityType(facet.entityType, generateParameters.serviceAVT);
    if (targetEntitySet) {
        // Make sure that custom columns definitions are created for passed entity
        customColumnDefinitionKey = ensureCustomColumnDefinitionExists(
            appSchema,
            generateParameters.manifest,
            targetEntitySet.name,
            facet.entityType.name,
            generateParameters.serviceAVT,
            facet,
            facetKey,
            generateParameters.fragments
        );
    }
    for (const facetItem in facets) {
        const facet = facets[facetItem];
        if (
            facet.base === FacetBase.LineItem &&
            (facet.ID === navigationProperty || facetItem.includes(navigationProperty))
        ) {
            addLineItemsType(
                true,
                appSchema,
                facet.target as AnnotationTerm<LineItem>,
                facet.entityType,
                facet.ID || facetItem,
                customColumnDefinitionKey
            );
        }
    }

    // Add manifestPath to table
    addManifestPathsToPropertiesOP(appSchema, undefined, tableDefinition, {
        manifest: generateParameters.manifest,
        pageName: pageKeys,
        sectionId: facetKey
    });
}

/**
 * Adds definitions for subsections in object page sections to the app schema.
 *
 * @param {Definition} appSchema - app schema in general
 * @param {string} definitionKey - key of app schema definition to be filled
 * @param {FacetConfig} facet facet or section information
 * @param {string} facetId - key of the facet, as listed in FacetConfigs
 * @param {SectionTypeV2} sectionType (sub)section type
 * @param isHeaderFacet - Indicates whether the facet is located in the header section
 */
function handleSubSection(
    appSchema: Definition,
    definitionKey: string,
    facet: FacetConfig,
    facetId: string,
    sectionType: SectionTypeV2,
    isHeaderFacet = false
): void {
    appSchema.definitions[definitionKey] = createDefaultSection(appSchema, facet, facetId);
    const appSchemaSection = appSchema.definitions[definitionKey];
    const sectionDefinition = isHeaderFacet ? SectionTypeV2.HeaderSection : SectionTypeV2.SubSection;
    addAppSchemaDefinition(appSchema, appSchemaSection, sectionType, sectionDefinition);
    appSchemaSection.properties.subsections = {
        type: 'object',
        description: 'Subsections',
        properties: {},
        additionalProperties: false
    };
    appSchemaSection.properties.subsections[SchemaTag.isViewNode] = true;
}

/**
 * Method fills new chart section with properties.
 *
 * @param {Definition} appSchema App schema in general
 * @param {string} definitionKey - key of app schema definition to be filled
 * @param {FacetConfig} facet facet or section information
 * @param {string} facetKey - facet key.
 * @param {SectionTypeV2} sectionType (sub)section type
 */
function handleSectionChart(
    appSchema: Definition,
    definitionKey: string,
    facet: FacetConfig,
    facetKey: string,
    sectionType: SectionTypeV2
) {
    appSchema.definitions[definitionKey] = createDefaultSection(appSchema, facet, facetKey);
    const appSchemaSection = appSchema.definitions[definitionKey];
    addAppSchemaDefinition(appSchema, appSchemaSection, sectionType, SectionTypeV2.SectionChart);
}
/**
 * Method fills new header section with properties.
 *
 * @param {Definition} appSchema App schema in general
 * @param {string} definitionKey - key of app schema definition to be filled
 * @param {FacetConfig} facet facet or section information
 * @param {string} facetKey - facet key.
 * @param {SectionTypeV2} sectionType (sub)section type
 * @param facetDefinition - The type of the facet definition to be used for the header facet
 */
function handleHeaderFacet(
    appSchema: Definition,
    definitionKey: string,
    facet: FacetConfig,
    facetKey: string,
    sectionType: SectionTypeV2,
    facetDefinition?: SectionTypeV2
) {
    appSchema.definitions[definitionKey] = createDefaultSection(appSchema, facet, facetKey);
    const appSchemaSection = appSchema.definitions[definitionKey];
    addAppSchemaDefinition(
        appSchema,
        appSchemaSection,
        sectionType,
        facetDefinition || SectionTypeV2.CommonHeaderFacetSettings
    );
    appSchemaSection[SchemaTag.annotationPath] = facet.annotationPath;
    appSchemaSection[SchemaTag.isViewNode] = true;
    appSchemaSection[SchemaTag.dataType] = determineDataTypeOfTarget(facet?.target);
}

/**
 * Method fills new header section with properties.
 *
 * @param appSchema - The application schema to be updated
 * @param definitionKey - The key of the definition to be filled
 * @param facet - The facet or section information
 */
function addDataPointHeaderFacetDataType(appSchema: Definition, definitionKey: string, facet: FacetConfig) {
    const appSchemaSection = appSchema.definitions[definitionKey] as Definition;
    const dataType = (appSchemaSection[SchemaTag.dataType] = determineDataTypeOfTarget(facet?.target));
    if (dataType === DataType.Rating) {
        appSchemaSection.title = `${appSchemaSection.title}::RatingIndicatorVBox`;
    } else if (dataType === DataType.Progress) {
        appSchemaSection.title = `${appSchemaSection.title}::ProgressIndicatorVBox`;
    } else {
        appSchemaSection.title = `${appSchemaSection.title}::DataPoint`;
    }
}

/**
 * Method fills new form section in app schema with properties.
 *
 * @param {Definition} appSchema App schema in general
 * @param {string} definitionKey - key of app schema definition to be filled
 * @param {FacetConfig} facet facet or section information
 * @param {string} facetKey - facet key.
 * @param {SectionTypeV2} sectionType (sub)section type
 * @returns {object} returns filled form section in app schema.
 */
function handleFormSection(
    appSchema: Definition,
    definitionKey: string,
    facet: FacetConfig,
    facetKey: string,
    sectionType: SectionTypeV2
) {
    appSchema.definitions[definitionKey] = createDefaultSection(appSchema, facet, facetKey);
    const appSchemaSection = appSchema.definitions[definitionKey];
    addAppSchemaDefinition(appSchema, appSchemaSection, sectionType, SectionTypeV2.SectionForm);
    appSchemaSection[SchemaTag.annotationPath] = facet.annotationPath;
    appSchemaSection[SchemaTag.isViewNode] = true;
    return appSchemaSection;
}

/**
 * Mapping of facet type to (sub)section type to enable correct further handling.
 *
 * @param facetType - facet type to be mapped
 * @param isSubSection - if section is subsection of section
 * @param isHeaderFacet - Indicates whether the facet is located in the header section
 * @returns {SectionTypeV2} sectionType (sub)section type
 */
function getSectionType(facetType: FacetBase, isSubSection: boolean, isHeaderFacet: boolean) {
    const headerSectionMap = new Map<FacetBase, SectionTypeV2>([
        [FacetBase.CollectionFacet, SectionTypeV2.HeaderSection],
        [FacetBase.Address, SectionTypeV2.HeaderSectionAddress],
        [FacetBase.Chart, SectionTypeV2.HeaderSectionChart],
        [FacetBase.DataPoint, SectionTypeV2.HeaderSectionDataPoint],
        [FacetBase.Form, SectionTypeV2.HeaderSectionForm],
        [FacetBase.Identification, SectionTypeV2.HeaderSectionForm]
    ]);
    const sectionTypeMapping = new Map<FacetBase, SectionTypeV2>([
        [FacetBase.CollectionFacet, SectionTypeV2.Section],
        [FacetBase.LineItem, SectionTypeV2.SectionTable],
        [FacetBase.Chart, SectionTypeV2.SectionChart],
        [FacetBase.Address, SectionTypeV2.SectionAddress],
        [FacetBase.Contact, SectionTypeV2.SectionContact],
        [FacetBase.Form, SectionTypeV2.SectionForm],
        [FacetBase.Identification, SectionTypeV2.SectionForm]
    ]);
    const subSectionTypeMapping = new Map<FacetBase, SectionTypeV2>([
        [FacetBase.CollectionFacet, SectionTypeV2.SubSection],
        [FacetBase.LineItem, SectionTypeV2.SubSectionTable],
        [FacetBase.Chart, SectionTypeV2.SubSectionChart],
        [FacetBase.Address, SectionTypeV2.SubSectionAddress],
        [FacetBase.Contact, SectionTypeV2.SubSectionContact],
        [FacetBase.Form, SectionTypeV2.SubSectionForm],
        [FacetBase.Identification, SectionTypeV2.SubSectionForm]
    ]);
    if (isHeaderFacet) {
        return headerSectionMap.get(facetType);
    } else if (isSubSection) {
        return subSectionTypeMapping.get(facetType);
    } else {
        return sectionTypeMapping.get(facetType);
    }
}

/**
 * Method returns reference key, special handling of (sub)sections.
 *
 * @param {FacetConfig} facet facet or section information
 * @param {string} facetKey - facet key.
 * @param {SectionTypeV2} sectionType (sub)section type
 * @returns {string} reference key
 */
function getReferenceKey(facet: FacetConfig, facetKey, sectionType): string {
    let referenceKey = facet.ID || facetKey;
    if (sectionType === (SectionTypeV2.Section || SectionTypeV2.SubSection)) {
        referenceKey = referenceKey.replace(`@${UIAnnotationTerms.Facets}`, facet.base);
    }
    return `${referenceKey}`;
}

/**
 * Creates a section definition in app schema.
 *
 * @param pageKeys Array path to target page (e.g., ['parentPage', 'currentPage'])
 * @param generateParameters - list of API input parameters
 * @param facets - list of all facets.
 * @param facetKey - facet key.
 * @param sections - schema of current sections definition.
 * @param appSchema - app specific schema that potentially gets enhanced.
 * @param isSubSection - if section is subsection of section
 * @param isHeaderFacet - Indicates whether the facet is located in the header section
 */
function addSection(
    pageKeys: string[],
    generateParameters: GenerateAppSchemaParameters,
    facets: FacetConfigs,
    facetKey: string,
    sections: Definition,
    appSchema: Definition,
    isSubSection = false,
    isHeaderFacet = false
): void {
    const facet: FacetConfig = facets[facetKey];
    const sectionType = getSectionType(facet.base, isSubSection, isHeaderFacet);
    const referenceKey = getReferenceKey(facet, facetKey, sectionType);
    const definitionKey = `${sectionType}<${referenceKey}>`;
    addSectionReference(sections, referenceKey, definitionKey);
    switch (sectionType) {
        case SectionTypeV2.Section:
        case SectionTypeV2.SubSection:
        case SectionTypeV2.HeaderSection: {
            //handle collection facets
            isSubSection = isHeaderFacet ? false : true;
            handleSubSection(appSchema, definitionKey, facet, facetKey, sectionType, isHeaderFacet);
            const subSections = (appSchema.definitions[definitionKey] as Definition).properties
                .subsections as Definition;
            for (const key in facet.facets) {
                addSection(
                    pageKeys,
                    generateParameters,
                    facet.facets,
                    key,
                    subSections,
                    appSchema,
                    isSubSection,
                    isHeaderFacet
                );
            }
            break;
        }
        case SectionTypeV2.SectionTable:
        case SectionTypeV2.SubSectionTable: {
            handleLineItem(pageKeys, generateParameters, referenceKey, appSchema, facet, facets, sectionType);
            //add fields for createWithParameterDialog
            const floorplanSuffix = 'OP';
            addFieldsType(appSchema, facet.entityType, floorplanSuffix);
            break;
        }
        case SectionTypeV2.SectionChart:
        case SectionTypeV2.SubSectionChart: {
            //handle reference facet (if comprising chart)
            handleSectionChart(appSchema, definitionKey, facet, referenceKey, sectionType);
            addManifestPathsToDefinitionPropertiesOP(
                appSchema,
                pageKeys,
                definitionKey,
                generateParameters,
                referenceKey
            );
            break;
        }
        case SectionTypeV2.SectionForm:
        case SectionTypeV2.SubSectionForm: {
            const appSchemaSection = handleFormSection(appSchema, definitionKey, facet, referenceKey, sectionType);
            handleForm(SectionType.Section, facet, appSchemaSection, appSchema, FioriElementsVersion.v2, facetKey);
            break;
        }
        case SectionTypeV2.SectionAddress:
        case SectionTypeV2.SectionContact: {
            handleAddressContact(facet, referenceKey, sections, appSchema, SectionType.Section, undefined, 'V2');
            break;
        }
        case SectionTypeV2.SubSectionAddress:
        case SectionTypeV2.SubSectionContact: {
            handleAddressContact(
                facet,
                referenceKey,
                sections,
                appSchema,
                SectionType.Section,
                SectionType.SubSection,
                'V2'
            );
            break;
        }
        case SectionTypeV2.HeaderSectionForm:
        case SectionTypeV2.HeaderSectionChart:
        case SectionTypeV2.HeaderSectionAddress: {
            handleHeaderFacet(appSchema, definitionKey, facet, referenceKey, sectionType);
            break;
        }
        case SectionTypeV2.HeaderSectionDataPoint: {
            handleHeaderFacet(appSchema, definitionKey, facet, referenceKey, sectionType);
            addDataPointHeaderFacetDataType(appSchema, definitionKey, facet);
            break;
        }
        default: {
            // Other facets -> only default properties of ObjectPageSection
            sections.properties[facetKey] = createDefaultSection(appSchema, facet, referenceKey);
        }
    }
}

/**
 * Helper function to add facets to the app schema.
 *
 * @param pageKeys Array path to target page (e.g., ['parentPage', 'currentPage'])
 * @param facetsExists flag if facets exists and should be added
 * @param facets collection of facets
 * @param sections sections in config to be extended
 * @param generateParameters list of API input parameters
 * @param appSchema app specific schema that potentially gets enhanced
 * @param isHeaderFacet flag if facet is located in header section
 */
function addSectionsFromFacets(
    pageKeys: string[],
    facetsExists: boolean,
    facets: FacetConfigs,
    sections: Definition,
    generateParameters: GenerateAppSchemaParameters,
    appSchema: Definition,
    isHeaderFacet = false
) {
    if (!facetsExists) {
        return;
    }
    for (const facetKey in facets) {
        addSection(pageKeys, generateParameters, facets, facetKey, sections, appSchema, false, isHeaderFacet);
    }
}

/**
 * Adds the sections to the app schema.
 *
 * @param pageKeys Array path to target page (e.g., ['parentPage', 'currentPage'])
 * @param generateParameters - list of API input parameters
 * @param appSchema - app specific schema that potentially gets enhanced
 * @param pages - list of (manifest) pages
 */
function addSections(
    pageKeys: string[],
    generateParameters: GenerateAppSchemaParameters,
    appSchema: Definition,
    pages: v2.SapUiAppPagesV2
): void {
    const sections = appSchema.definitions[DefinitionName.Sections] as Definition;
    sections[SchemaTag.isViewNode] = true;
    if (generateParameters.entitySet?.entityType) {
        sections[SchemaTag.annotationPath] =
            `/${generateParameters.entitySet.entityType.fullyQualifiedName}/@${UIAnnotationTerms.Facets}`;
    }
    const headerSections = appSchema.definitions[DefinitionName.HeaderSections] as Definition;
    headerSections[SchemaTag.isViewNode] = true;
    if (!headerSections.properties) {
        headerSections.properties = {};
    }
    headerSections.additionalProperties = false;
    if (generateParameters.entitySet?.entityType) {
        headerSections[SchemaTag.annotationPath] =
            `/${generateParameters.entitySet.entityType.fullyQualifiedName}/@${UIAnnotationTerms.HeaderFacets}`;
    }

    if (!pages) {
        return;
    }
    // Get facet annotations
    const facets: FacetConfigs = getObjectPageFacets(
        generateParameters.entitySet?.entityType,
        generateParameters.serviceAVT,
        FioriElementsVersion.v2,
        generateParameters.logger
    );
    const headerFacets: FacetConfigs = getObjectPageHeaderFacets(
        generateParameters.entitySet?.entityType,
        generateParameters.serviceAVT,
        FioriElementsVersion.v2,
        generateParameters.logger
    );
    const generateFacets = facets && !!Object.keys(facets).length;
    const generateHeaderFacets = headerFacets && !!Object.keys(headerFacets).length;
    if (!generateFacets && !generateHeaderFacets) {
        return;
    }

    // Loop on all pages, look for the right entity set
    for (const key in pages) {
        const element = pages[key];
        if (generateParameters.entitySet?.name?.includes(element.entitySet)) {
            // Loop on all facets (of the given entitySet)
            addSectionsFromFacets(pageKeys, generateFacets, facets, sections, generateParameters, appSchema);
            addSectionsFromFacets(
                pageKeys,
                generateHeaderFacets,
                headerFacets,
                headerSections,
                generateParameters,
                appSchema,
                true
            );
        } else {
            addSections(pageKeys, generateParameters, appSchema, element.pages);
        }
    }

    // Add custom sections
    const customSections = appSchema.definitions['CustomSections'] as Definition;
    for (const name in customSections.properties) {
        sections.properties[name] = customSections.properties[name];
    }
}

/**
 * Add Object Page Header to app-specific schema.
 *
 * @param appSchema Schema of the app
 * @param generateParameters - list of API input parameters
 * @param entityType - current entity type
 */
function addHeader(appSchema: object, generateParameters: GenerateAppSchemaParameters, entityType: EntityType): void {
    addCommonHeaderSchema(appSchema, entityType);

    //Add header actions
    addHeaderActions(appSchema, entityType, generateParameters, addHeaderActionCallBackV2, undefined, true);
}

/**
 * Adds the related facets keys 'enum' to the app schema.
 *
 * @param schema - app specific schema that potentially gets enhanced
 * @param {EntityType} entityType - current entity type
 * @param {ConvertedMetadata} oDataServiceAVT - complete service information, as returned by annotation vocabularies tool
 * @param {ExtensionLogger} logger - Logger class for logging messages
 */
function addRelatedFacetKeysType(
    schema: object,
    entityType: EntityType,
    oDataServiceAVT: ConvertedMetadata,
    logger?: ExtensionLogger
): void {
    // Find sections from annotation
    // And create new definition in schema as enum with description
    const facetSections = getObjectPageFacetSection(entityType, oDataServiceAVT, logger, undefined, true);
    if (facetSections) {
        addDefinitionForRelatedFacetKeys(
            schema,
            ['ObjectPageCustomSectionFragment', 'ObjectPageCustomSectionView'],
            facetSections,
            ['ID', 'key']
        );
    }
}

/**
 * Adds 'manifestPath' to each property in a definition based on metadata sync rules.
 *
 * @param appSchema - Full schema.
 * @param definitionName - The name of the definition/class whose properties are being processed.
 * @param definition - The definition object containing property schemas.
 * @param pathParams - Additional context required to compute manifest paths.
 * @param parentProperty - Information about the parent property used when resolving.
 *   nested or inherited manifest paths (e.g. parent property name and resolved rule path).
 * @param handleRelative - A flag indicating whether to use relative paths for definition properties.
 */
function addManifestPathsToPropertiesOP(
    appSchema: Definition,
    definitionName: string,
    definition: Definition,
    pathParams: ManifestPathParams,
    parentProperty?: ManifestPropertyResolution,
    handleRelative = false
): void {
    addManifestPathsToProperties(
        appSchema,
        definitionName,
        definition,
        {
            ...pathParams,
            targetAnnotation: pathParams.sectionId?.replace('::', '/'),
            targetAnnotationEncoded: pathParams.sectionId,
            pathsParts: [pathParams.sectionId]
        },
        pageTypes.ObjectPage,
        parentProperty,
        handleRelative
    );
}

/**
 * Adds 'manifestPath' to static nodes in schema.
 *
 * @param appSchema - Full schema.
 * @param pathParams - Additional context required to compute manifest paths.
 */
function addManifestPathsToStaticNodes(appSchema: Definition, pathParams: ManifestPathParams): void {
    // Add "manifestPath" for current page
    appSchema['manifestPath'] = getPageManifestPath(pathParams);
    // Add manifestPath to root level
    addManifestPathsToPropertiesOP(appSchema, 'ObjectPage', appSchema, pathParams);
    // Add manifestPath to table
    addManifestPathsToPropertiesOP(appSchema, undefined, appSchema.properties.header as Definition, pathParams);
    // Add "manifestPath" to custom sections
    const sections = appSchema.definitions[DefinitionName.Sections]['properties'] ?? {};
    for (const sectionDefinitionKey in sections) {
        if (sectionDefinitionKey === 'custom') {
            const sectionDefinition = sections[sectionDefinitionKey] as Definition;
            const anyOfDefinitions = sectionDefinition.items['anyOf'] as Definition[];
            anyOfDefinitions.forEach((anyOfDefinition) => {
                const manifestPathForSections = convertSyncRulePathToJsonPath(getViewExtensionsPath());
                anyOfDefinition['manifestPath'] = appendJsonPathSegment(manifestPathForSections, `||`);
                sectionDefinition['manifestPath'] = manifestPathForSections;
                const definitionKey = anyOfDefinition.$ref ? getDefinitionKey(anyOfDefinition.$ref) : undefined;
                addManifestPathsToPropertiesOP(
                    appSchema,
                    definitionKey, // we need to pass definitionName to specifically resolve className and sync rules
                    anyOfDefinition,
                    { pageName: [''], sectionId: '' }, // we can't use undefined because these params are expected to be defined in sync rule resolution (check 'getObjectPageCustomSectionPath')
                    {
                        name: v2.SAPUI5_VIEW_EXTENSION_OBJECT_PAGE,
                        relativeManifestPath: v2.SAPUI5_VIEW_EXTENSION_OBJECT_PAGE,
                        absoluteManifestPath: manifestPathForSections
                    },
                    true
                );
                // remove manifestPath from anyOf definition because it was only required in resolving specific property paths (e.g. title)
                delete anyOfDefinition['manifestPath'];
            });
        }
    }
}

/**
 * Enhances the Object Page schema with manifest paths for definition and properties.
 *
 * This function extends the app schema (`appSchema`) by adding manifestPath to definition and properties on the Object Page.
 *
 * @param {Definition} appSchema - The application schema object representing the Object Page structure.
 * @param {string} pageKeys Array path to target page (e.g., ['parentPage', 'currentPage'])
 * @param {string} definitionName - Identifier of the corresponding definition.
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters.
 * @param {string} facetKey - Identifier of the section (facet key).
 */
function addManifestPathsToDefinitionPropertiesOP(
    appSchema: Definition,
    pageKeys: string[],
    definitionName: string,
    generateParameters: GenerateAppSchemaParameters,
    facetKey?: string
): void {
    const matchingDefinition = appSchema.definitions[definitionName] as Definition;
    const properties = matchingDefinition?.properties ?? {};

    for (const key in properties) {
        const pathParams: ManifestPathParams = {
            pageName: pageKeys,
            manifest: generateParameters.manifest,
            sectionId: facetKey,
            columnKey: undefined,
            originalIds: undefined
        };
        const property = matchingDefinition.properties[key] as Definition;
        addManifestPathsToPropertiesOP(appSchema, undefined, property, pathParams, undefined, true);
    }
}

/**
 * Generates an app specific schema out of the generic schema.
 *
 * @param generateParameters - list of API input parameters
 * @param genericSchema - generic JSON schema of an object page
 * @returns the app specific JSON schema
 */
export function generateObjectPageSchemaV2(
    generateParameters: GenerateAppSchemaParameters,
    genericSchema: object
): object {
    // Initialize i18next
    initI18n();

    const appSchema = JSON.parse(JSON.stringify(genericSchema)) as Definition;

    // Custom sections as part of sections
    const additionalSections = {};
    for (const name in appSchema.definitions['CustomSections']['properties']) {
        additionalSections[name] = (appSchema.definitions['CustomSections'] as Definition).properties[name];
    }

    // Change reference to generated sections
    appSchema.properties[PropertyName.sections] = {
        $ref: DEFINITION_LINK_PREFIX + 'Sections'
    };

    const entityType = generateParameters.entitySet?.entityType;
    const entitySetName = generateParameters.entitySet?.name;

    // Ensure that only those flex changes are used that refer to the current OP
    generateParameters.flex =
        generateParameters.entitySet && generateParameters.flex
            ? filterFlexChanges(
                  generateParameters.flex,
                  SAPUI5_VIEW_EXTENSION_OBJECT_PAGE,
                  generateParameters.entitySet.name
              )
            : [];

    generateParameters.entityType = entityType;
    const pages = generateParameters.manifest[ManifestSection.generic].pages;
    if (!pages) {
        log(generateParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOPAGES', { appId: generateParameters.manifest['sap.app']['id'] }),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.generic]
            }
        });
        return appSchema;
    }
    // Calculate page keys -> full path to current page (e.g., ['parentPage', 'currentPage'])
    const pageKeys = [];
    findObjectPageV2(
        generateParameters.manifest?.[ManifestSection.generic]?.pages as v2.SapUiAppPagesV2,
        entitySetName,
        pageKeys
    );

    // Add header
    addHeader(appSchema, generateParameters, entityType);

    // Add sections
    appSchema.definitions[DefinitionName.Sections] = {
        type: 'object',
        properties: {},
        description: 'Sections',
        additionalProperties: false
    };
    addSections(pageKeys, generateParameters, appSchema, pages as v2.SapUiAppPagesV2);

    // Custom section facets
    addRelatedFacetKeysType(appSchema, entityType, generateParameters.serviceAVT, generateParameters.logger);

    if (!appSchema.definitions.Sections) {
        if (appSchema.definitions.RelatedFacetKeys && appSchema.definitions.RelatedFacetKeys['oneOf'].length) {
            // If related facets exists - then we need sections definition
            appSchema.definitions.Sections = {
                type: 'object',
                properties: additionalSections,
                additionalProperties: false
            };
            appSchema.definitions.Sections[SchemaTag.isViewNode] = true;
        } else {
            delete appSchema.properties[PropertyName.sections];
        }
    }
    addFooterActions(
        appSchema,
        generateParameters,
        FioriElementsVersion.v2,
        addFooterActionCallBackV2,
        pages as v2.SapUiAppPagesV2
    );

    // Add manifestPath to table
    addManifestPathsToStaticNodes(appSchema, {
        manifest: generateParameters.manifest,
        pageName: pageKeys
    });

    // Eliminate generic definitions
    delete appSchema.definitions['GenericSections'];
    delete appSchema.definitions['GenericSubSections'];
    delete appSchema.definitions['ObjectPageSectionTableV2'];
    delete appSchema.definitions['GenericColumns'];
    delete appSchema.definitions['TableColumnAction'];
    delete appSchema.definitions['CommonHeaderFacetSettings'];
    delete appSchema.definitions['ObjectPageHeaderSection'];
    // Currently not used
    delete appSchema.definitions['ObjectPageCustomHeaderSectionFragment'];
    delete appSchema.definitions['ObjectPageSectionFormV2'];
    delete appSchema.definitions['ObjectPageSectionTableV2'];
    delete appSchema.definitions['ObjectPageSubSectionV2'];
    delete appSchema.definitions['ObjectPageResponsiveTableWithMultiSelect'];
    delete appSchema.definitions['ObjectPageResponsiveTableWithInlineDelete'];
    delete appSchema.definitions['ObjectPageAnalyticalTable'];
    delete appSchema.definitions['ObjectPageTreeTable'];
    delete appSchema.definitions['ObjectPageGridTable'];
    delete appSchema.definitions['ObjectPageToolBar'];
    delete appSchema.definitions['ObjectPageToolBarActions'];
    delete appSchema.definitions['ObjectPageForm'];
    delete appSchema.definitions['ObjectPageFormFields'];
    delete appSchema.definitions['ObjectPageFormActions'];
    delete appSchema.definitions['FieldPathOP'];
    updatePropertyIndices(appSchema, ROOT_PROPERTIES_ORDER);
    return appSchema;
}
